/**
 * The configurable "standard" fields on the storefront customer-registration
 * form (PD-14370).
 *
 * The always-on basic contact fields — First Name, Last Name, Email, Phone —
 * are intentionally NOT listed here: they are always shown and always required,
 * and cannot be toggled (a lead is useless without a way to reach the person).
 * Everything below is the "beyond basic customer information" a tenant may or
 * may not want to collect, so each field is individually toggleable
 * (visible / hidden + required / optional) per tenant and per portal via the
 * `registration_standard_field` config table.
 *
 * This list IS the default set — it ships in code, so every instance gets these
 * defaults automatically with no per-tenant data to seed or backfill. A tenant
 * only writes a `registration_standard_field` row when it overrides a default;
 * absent a row, the code default here applies (see `resolveStandardFieldConfig`).
 *
 * `fieldKey` matches the storefront form's field key (and, for the mapped
 * fields, the client column the answer lands in). The `label` is the
 * admin-facing name shown in the settings builder; the storefront keeps its own
 * translated labels.
 */
export interface StandardRegistrationFieldMeta {
	fieldKey: string;
	label: string;
	defaultVisible: boolean;
	defaultRequired: boolean;
}

export const STANDARD_REGISTRATION_FIELDS: ReadonlyArray<StandardRegistrationFieldMeta> =
	[
		{
			fieldKey: 'companyName',
			label: 'Company Name',
			defaultVisible: true,
			defaultRequired: true,
		},
		{
			fieldKey: 'address1',
			label: 'Street Address',
			defaultVisible: true,
			defaultRequired: true,
		},
		{
			fieldKey: 'address2',
			label: 'Address Line 2',
			defaultVisible: true,
			defaultRequired: false,
		},
		{
			fieldKey: 'city',
			label: 'City',
			defaultVisible: true,
			defaultRequired: true,
		},
		{
			fieldKey: 'state',
			label: 'State',
			defaultVisible: true,
			defaultRequired: true,
		},
		{
			fieldKey: 'zip',
			label: 'ZIP',
			defaultVisible: true,
			defaultRequired: true,
		},
		{
			fieldKey: 'businessType',
			label: 'Business Type',
			defaultVisible: true,
			defaultRequired: false,
		},
		{
			fieldKey: 'leadSource',
			label: 'How did you hear about us?',
			defaultVisible: true,
			defaultRequired: false,
		},
		{
			fieldKey: 'notes',
			label: 'Anything else we should know?',
			defaultVisible: true,
			defaultRequired: false,
		},
	] as const;

export const STANDARD_REGISTRATION_FIELD_KEYS: ReadonlyArray<string> =
	STANDARD_REGISTRATION_FIELDS.map((field) => field.fieldKey);

/** Effective config for one standard field after merging defaults + overrides. */
export interface StandardRegistrationFieldConfigDto {
	fieldKey: string;
	/** Admin-facing label (from the code default set). */
	label: string;
	isVisible: boolean;
	isRequired: boolean;
}

/**
 * Minimal shape one config row must expose for resolution. `isVisible` /
 * `isRequired` are typed `boolean | number` on purpose: they map to MySQL
 * `tinyint(1)` columns, which TypeORM hydrates as the numbers 0/1, not real
 * booleans — the resolver coerces them so consumers always get true booleans.
 */
export interface StandardRegistrationFieldRow {
	fkPortalSiteId: number | null;
	fieldKey: string;
	isVisible: boolean | number;
	isRequired: boolean | number;
}

/**
 * Resolve the effective standard-field config for a scope by layering, per
 * field independently: code default → tenant-default row (NULL portal) →
 * this portal's row. Per-field merge (unlike the all-or-nothing supplemental
 * resolver) so a portal can override a single field without re-declaring the
 * rest. `portalSiteId == null` resolves the tenant-default scope (code default
 * overlaid with tenant-default rows only).
 */
export function resolveStandardFieldConfig(
	rows: ReadonlyArray<StandardRegistrationFieldRow>,
	portalSiteId: number | null,
): Array<StandardRegistrationFieldConfigDto> {
	const tenantDefault = new Map<string, StandardRegistrationFieldRow>();
	const portalOverride = new Map<string, StandardRegistrationFieldRow>();
	for (const row of rows) {
		if (row.fkPortalSiteId == null) {
			tenantDefault.set(row.fieldKey, row);
		} else if (portalSiteId != null && row.fkPortalSiteId === portalSiteId) {
			portalOverride.set(row.fieldKey, row);
		}
	}

	return STANDARD_REGISTRATION_FIELDS.map((meta) => {
		const effective =
			portalOverride.get(meta.fieldKey) ?? tenantDefault.get(meta.fieldKey);
		return {
			fieldKey: meta.fieldKey,
			label: meta.label,
			// Coerce the tinyint 0/1 to a real boolean (see the row-type note).
			isVisible:
				effective != null ? Boolean(effective.isVisible) : meta.defaultVisible,
			isRequired:
				effective != null
					? Boolean(effective.isRequired)
					: meta.defaultRequired,
		};
	});
}
